// main.jsx - PyK7VM Homepage
// Compiled to dist/main.js via build.mjs.

const { useState, useEffect, useRef, useCallback } = React;
const { Reveal, WordCycle, useScrollRef, useInView } = window;
const { Nav, Footer, DiscordCTA } = window;

function SvgCheck({ size = 14, color = '#10b981' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function SvgCross({ size = 14, color = '#f87171' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function SvgArrowRight({ size = 14, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

// ─── Smooth scroll helper ───────────────────────────────────────────────────
function smoothScrollTo(targetId) {
  const el = document.getElementById(targetId);
  if (!el) return;
  if (window.__lenis) {
    window.__lenis.scrollTo(el, { offset: -60, duration: 0.8 });
  } else {
    el.scrollIntoView({ behavior: 'smooth' });
  }
}

// ─── WordCycle Actions ──────────────────────────────────────────────────────
const CYCLE_ACTION_VERBS = [
  'virtualized',
  'encrypted',
  'hardened',
  'protected',
  'shielded',
  'isolated',
];

// ─── Vertical Timeline Data ──────────────────────────────────────────────────
const TIMELINE_PHASES = [
  {
    num: '01',
    title: '01. Source Parsing & Analysis',
    desc: 'Analyzes your Python source syntax, maps function dependencies, and prepares your proprietary logic for native virtualization.',
    code: 'Input Source (app.py):\n  def verify_license(key, machine_id):\n      digest = sha256(key + machine_id)\n      return validate_digest(digest)',
    color: '#93c5fd',
  },
  {
    num: '02',
    title: '02. Bytecode Elimination',
    desc: 'Strips standard Python code objects, variable names, and string tables from protected functions. No .pyc artifacts are emitted on disk for them.',
    code: 'Bytecode Stripping:\n  [-] Standard PyCodeObject stripped\n  [-] Variable names anonymized\n  [✓] Zero .pyc bytecode generated',
    color: '#60a5fa',
  },
  {
    num: '03',
    title: '03. Polymorphic VM Synthesis',
    desc: 'Generates a private virtual machine with randomized opcode numbers, encrypted registers, and unique dispatch handlers for every build.',
    code: 'Synthesized VM Loop (Build #9F2A):\n  0x00: PUSH_ENC_CONST [0x9F, seed=0x88]\n  0x04: VM_MUTATE_OP   r0, r3\n  0x08: DISPATCH_INNER handler_0x7fa3',
    color: '#a855f7',
  },
  {
    num: '04',
    title: '04. Native C Compilation',
    desc: 'Compiles the custom VM dispatcher and bytecode payloads directly into native machine code (.pyd / .so) via optimized C compilers.',
    code: 'Native Assembly (x86_64):\n  mov  rax, [rsp+0x18]\n  xor  r8d, 0x88b1\n  jmp  qword ptr [r12+rax*8]',
    color: '#10b981',
  },
  {
    num: '05',
    title: '05. Integrity Sealing & Anti-Debug',
    desc: 'Binds debugger-attachment detection, out-of-process memory-read monitoring, and integrity checks into the compiled binary.',
    code: 'Output: dist/app.pyd\n  [✓] Anti-debug trap handlers armed\n  [✓] Transient constant encryption primed\n  [✓] Sealed Native Extension Ready',
    color: '#ec4899',
  },
];

// ─── Bento Grid Capabilities ────────────────────────────────────────────────
const BENTO_FEATURES = [
  {
    title: 'Custom Stack Virtual Machine',
    tag: 'Zero Bytecode',
    desc: 'Protected functions are compiled into custom VM bytecode executed by a private native C engine. No standard Python bytecode exists in the file or memory.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#93c5fd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="4" width="16" height="16" rx="2" />
        <rect x="9" y="9" width="6" height="6" />
        <line x1="9" y1="1" x2="9" y2="4" /><line x1="15" y1="1" x2="15" y2="4" />
        <line x1="9" y1="20" x2="9" y2="23" /><line x1="15" y1="20" x2="15" y2="23" />
      </svg>
    ),
    large: true,
  },
  {
    title: 'Per-Build Polymorphism',
    tag: 'Unique Signatures',
    desc: 'Every compilation assigns fresh randomized opcode numbers, registers, and cryptographic seeds. Analysis artifacts recovered from one build (opcode tables, handler offsets) do not transfer to the next.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#a855f7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
  },
  {
    title: 'Transient Constant Encryption',
    tag: 'Memory Protection',
    desc: 'Strings, API endpoints, and byte literals stay encrypted in the binary and are decrypted in RAM only while actively used, then wiped.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
    ),
  },
  {
    title: 'Active Anti-Debug & Self-Destruct',
    tag: 'Runtime Defense',
    desc: 'Monitors debugger attachment, memory snapshotting, and frame hooking. Automatically terminates execution or corrupts state upon tampering.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
    ),
  },
  {
    title: 'Native Hot-Path Acceleration',
    tag: 'Hot Paths',
    desc: 'Annotate security-critical logic for maximum virtualization, and compute-heavy matrix math with native bypass for full compiled C performance.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
      </svg>
    ),
  },
  {
    title: 'Enterprise CI/CD & REST API',
    tag: 'Automation',
    desc: 'Integrate automated protection directly into GitHub Actions, GitLab CI, or custom deployment pipelines using our native CLI and cloud REST endpoints.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
      </svg>
    ),
  },
];

// ─── HERO SECTION ───────────────────────────────────────────────────────────
function Hero() {
  const [activeTab, setActiveTab] = useState('sealed'); // 'source' | 'sealed'

  return (
    <section id="hero" style={{ position: 'relative', minHeight: '90vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'clamp(96px, 12vw, 140px) clamp(16px, 4vw, 32px) clamp(40px, 6vw, 80px)', overflow: 'hidden' }}>
      {/* Background ambient radial glow */}
      <div style={{ position: 'absolute', top: '-10%', left: '50%', transform: 'translateX(-50%)', width: 'clamp(320px, 90vw, 900px)', height: '600px', background: 'radial-gradient(ellipse at center, rgba(59,130,246,0.12) 0%, rgba(30,58,138,0.04) 50%, transparent 75%)', pointerEvents: 'none', zIndex: 0 }} />

      <div style={{ maxWidth: 1240, width: '100%', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.15fr 0.95fr', gap: 'clamp(28px, 4vw, 64px)', alignItems: 'center' }} className="hero-split-grid">
          
          {/* Left Column: Headline & Action */}
          <div>
            <Reveal delay={80}>
              <h1 style={{ fontSize: 'clamp(34px, 5.2vw, 76px)', fontWeight: 500, lineHeight: 1.08, letterSpacing: -2, color: 'var(--text)', textWrap: 'balance', marginBottom: 14, fontFamily: 'var(--font-ui)' }}>
                <span>Your Python code,</span>
                <span style={{ display: 'block', marginTop: 4 }}>
                  <WordCycle
                    words={CYCLE_ACTION_VERBS}
                    interval={2100}
                    duration={700}
                    wordStyle={{
                      fontFamily: 'var(--font-ui)',
                      fontStyle: 'normal',
                      fontWeight: 300,
                      backgroundImage: 'linear-gradient(180deg, var(--text) 0%, var(--brand) 100%)',
                      WebkitBackgroundClip: 'text',
                      backgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      color: 'transparent',
                      letterSpacing: -2,
                    }}
                  />
                  {' '}<span>by design.</span>
                </span>
              </h1>
            </Reveal>

            <Reveal delay={140}>
              <p style={{ color: 'var(--text-dim)', fontSize: 'clamp(15px, 2vw, 16.5px)', lineHeight: 1.65, maxWidth: 540, marginBottom: 32, fontFamily: 'var(--font-ui)' }}>
                PyK7VM strips Python AST and bytecode entirely, compiling proprietary logic into an opaque polymorphic native virtual machine. Disassemblers see only encrypted payloads and a private stack machine.
              </p>
            </Reveal>

            <Reveal delay={200}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <a
                  href="register.php"
                  style={{
                    background: 'var(--text)', color: 'var(--bg)', padding: '13px 26px',
                    borderRadius: 999, fontWeight: 600, fontSize: 14.5, textDecoration: 'none',
                    letterSpacing: -0.2, boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
                    transition: 'transform .2s, box-shadow .2s', fontFamily: 'var(--font-ui)',
                    minHeight: 46, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}
                >
                  Start Free Trial
                </a>

                <button
                  onClick={() => smoothScrollTo('pipeline')}
                  style={{
                    background: 'var(--surface)', color: 'var(--text-dim)', border: '1px solid var(--border-strong)',
                    padding: '13px 22px', borderRadius: 999, fontWeight: 500, fontSize: 14, cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 8, transition: 'all .2s', fontFamily: 'var(--font-ui)',
                    minHeight: 46,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-dim)'; }}
                >
                  <span>Explore Architecture</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
                </button>
              </div>
            </Reveal>
          </div>

          {/* Right Column: Interactive Terminal Preview Window */}
          <div style={{ minWidth: 0 }}>
            <Reveal delay={240}>
              <div
                className="hero-terminal-window stay-dark"
                style={{
                  background: '#09090f', border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 18, backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', padding: 'clamp(14px, 3vw, 24px)',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.6), 0 0 30px rgba(59,130,246,0.08)',
                  position: 'relative', overflow: 'hidden', width: '100%',
                }}
              >
                {/* Terminal Header with Manual Switcher Tabs */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 14, borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 5, background: '#ef4444' }} />
                    <div style={{ width: 10, height: 10, borderRadius: 5, background: '#f59e0b' }} />
                    <div style={{ width: 10, height: 10, borderRadius: 5, background: '#10b981' }} />
                  </div>

                  {/* Manual Two-State Tabs */}
                  <div style={{ display: 'flex', gap: 4, background: 'rgba(255,255,255,0.05)', padding: 3, borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)' }}>
                    <button
                      onClick={() => setActiveTab('source')}
                      style={{
                        background: activeTab === 'source' ? 'rgba(59,130,246,0.25)' : 'transparent',
                        color: activeTab === 'source' ? '#93c5fd' : '#a1a1aa',
                        border: activeTab === 'source' ? '1px solid rgba(59,130,246,0.4)' : '1px solid transparent',
                        borderRadius: 6, padding: '4px 8px', fontSize: 11, fontFamily: 'var(--font-code)', cursor: 'pointer',
                        transition: 'all .2s ease', minHeight: 28,
                      }}
                    >
                      Raw Source (.py)
                    </button>
                    <button
                      onClick={() => setActiveTab('sealed')}
                      style={{
                        background: activeTab === 'sealed' ? 'rgba(59,130,246,0.25)' : 'transparent',
                        color: activeTab === 'sealed' ? '#93c5fd' : '#a1a1aa',
                        border: activeTab === 'sealed' ? '1px solid rgba(59,130,246,0.4)' : '1px solid transparent',
                        borderRadius: 6, padding: '4px 8px', fontSize: 11, fontFamily: 'var(--font-code)', cursor: 'pointer',
                        transition: 'all .2s ease', minHeight: 28,
                      }}
                    >
                      Protected Native (.pyd)
                    </button>
                  </div>
                </div>

                {/* Code Terminal View with Smooth Transition */}
                <div key={activeTab} style={{ minHeight: 180, fontFamily: 'var(--font-code)', fontSize: 'clamp(11px, 2.2vw, 12.5px)', lineHeight: 1.75, overflowX: 'auto', WebkitOverflowScrolling: 'touch', animation: 'term-fade .25s ease both' }}>
                  {activeTab === 'source' ? (
                    <div>
                      <div style={{ color: '#a1a1aa', marginBottom: 8 }}># Input Python Source: module.py</div>
                      <div><span style={{ color: '#93c5fd' }}>def</span> <span style={{ color: '#f1f5f9' }}>verify_license</span>(key: str, machine_id: str):</div>
                      <div style={{ paddingLeft: 14, color: '#d4d4d8' }}>salt = <span style={{ color: '#93c5fd' }}>"enterprise-secret-seed-2026"</span></div>
                      <div style={{ paddingLeft: 14, color: '#d4d4d8' }}>token = sha256(f<span style={{ color: '#93c5fd' }}>"{'{key}'}:{'{machine_id}'}:{'{salt}'}"</span>).hexdigest()</div>
                      <div style={{ paddingLeft: 14, color: '#93c5fd' }}>return <span style={{ color: '#f1f5f9' }}>dispatch_validation</span>(token)</div>
                    </div>
                  ) : (
                    <div>
                      <div style={{ color: '#93c5fd', marginBottom: 8 }}>; Sealed Native Extension: module.pyd (x86_64)</div>
                      <div><span style={{ color: '#a1a1aa' }}>0x0040:</span> <span style={{ color: '#93c5fd' }}>mov</span>  <span style={{ color: '#f1f5f9' }}>rax, [rsp+0x18]</span>        <span style={{ color: '#a1a1aa' }}>; load payload</span></div>
                      <div><span style={{ color: '#a1a1aa' }}>0x0044:</span> <span style={{ color: '#93c5fd' }}>xor</span>  <span style={{ color: '#f1f5f9' }}>r8d, 0x88b1</span>            <span style={{ color: '#a1a1aa' }}>; mutate seed</span></div>
                      <div><span style={{ color: '#a1a1aa' }}>0x0048:</span> <span style={{ color: '#93c5fd' }}>jmp</span>  <span style={{ color: '#f1f5f9' }}>qword ptr [r12+rax*8]</span>  <span style={{ color: '#a1a1aa' }}>; dispatch VM</span></div>
                      <div><span style={{ color: '#a1a1aa' }}>0x004C:</span> <span style={{ color: '#93c5fd' }}>call</span> <span style={{ color: '#f1f5f9' }}>pk7vm_integrity_guard</span>   <span style={{ color: '#a1a1aa' }}>; anti-debug check</span></div>
                    </div>
                  )}
                </div>

                {/* Bottom Sealing Bar */}
                <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5, color: activeTab === 'sealed' ? '#10b981' : '#d4d4d8', fontFamily: 'var(--font-ui)' }}>
                    {activeTab === 'sealed' ? <SvgCheck size={13} color="#10b981" /> : <span style={{ width: 6, height: 6, borderRadius: 3, background: '#3b82f6' }} />}
                    <span>{activeTab === 'sealed' ? '0 Bytecode · Polymorphic VM Loop' : 'Standard Python Source'}</span>
                  </div>
                  <span style={{ fontSize: 11, fontFamily: 'var(--font-code)', color: '#a1a1aa' }}>x86_64</span>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes term-fade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        @media (max-width: 920px) { .hero-split-grid { grid-template-columns: 1fr !important; gap: 36px !important; } }
        @media (min-width: 921px) { .hero-split-grid { grid-template-columns: 1.15fr 0.95fr !important; gap: clamp(28px, 4vw, 64px) !important; } }
      `}</style>
    </section>
  );
}

// ─── SECTION 2: VERTICAL TIMELINE ARCHITECTURE ──────────────────────────────
function ArchitecturePipeline() {
  return (
    <section id="pipeline" style={{ padding: 'clamp(60px, 8vw, 100px) clamp(16px, 4vw, 32px)', position: 'relative' }}>
      <div style={{ maxWidth: 1120, margin: '0 auto' }}>
        <Reveal>
          <div style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto clamp(36px, 6vw, 60px)' }}>
            <h2 style={{ fontSize: 'clamp(28px, 4.5vw, 48px)', fontWeight: 500, letterSpacing: -1.5, color: 'var(--text)', marginBottom: 14, fontFamily: 'var(--font-ui)' }}>
              How PyK7VM works.
            </h2>
            <p style={{ color: 'var(--text-dim)', fontSize: 'clamp(14.5px, 2vw, 16.5px)', lineHeight: 1.6, fontFamily: 'var(--font-ui)' }}>
              From raw Python source to a per-build native virtual machine in 5 automated phases.
            </p>
          </div>
        </Reveal>

        {/* Vertical Timeline List */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {TIMELINE_PHASES.map((p, idx) => {
            const isLast = idx === TIMELINE_PHASES.length - 1;
            return (
              <Reveal key={p.num} delay={idx * 50}>
                <div style={{ display: 'grid', gridTemplateColumns: '44px 1fr', gap: 'clamp(14px, 3vw, 28px)' }} className="timeline-grid-row">
                  {/* Left Column: Number Node & Connecting Line */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div
                      style={{
                        width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'rgba(10,10,15,0.95)', border: `1px solid ${p.color}`, color: p.color,
                        fontSize: 13, fontFamily: 'var(--font-code)', fontWeight: 700,
                        boxShadow: `0 0 14px ${p.color}33`, flexShrink: 0,
                      }}
                    >
                      {p.num}
                    </div>
                    {!isLast && (
                      <div
                        style={{
                          width: 2, flexGrow: 1, minHeight: 28, marginTop: 8, marginBottom: 8,
                          background: 'linear-gradient(to bottom, rgba(59,130,246,0.5), rgba(59,130,246,0.15))',
                        }}
                      />
                    )}
                  </div>

                  {/* Right Content Card */}
                  <div
                    style={{
                      background: 'var(--surface)', border: '1px solid var(--border)',
                      borderRadius: 16, padding: 'clamp(18px, 3vw, 26px)', marginBottom: isLast ? 0 : 24,
                      display: 'grid', gridTemplateColumns: '1.2fr 1.8fr', gap: 'clamp(16px, 3vw, 28px)', alignItems: 'center',
                      backdropFilter: 'blur(10px)', transition: 'border-color .3s ease', minWidth: 0,
                    }}
                    className="card timeline-card-split"
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(59,130,246,0.4)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}
                  >
                    <div>
                      <h3 style={{ fontSize: 'clamp(17px, 2.5vw, 19px)', fontWeight: 600, color: 'var(--text)', letterSpacing: -0.4, marginBottom: 8, fontFamily: 'var(--font-ui)' }}>
                        {p.title}
                      </h3>
                      <p style={{ color: 'var(--text-dim)', fontSize: 13.5, lineHeight: 1.6, margin: 0, fontFamily: 'var(--font-ui)' }}>
                        {p.desc}
                      </p>
                    </div>

                    <div className="terminal-box stay-dark" style={{ background: '#090912', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '14px 16px', fontFamily: 'var(--font-code)', fontSize: 11, lineHeight: 1.65, color: '#f1f5f9', overflowX: 'auto', WebkitOverflowScrolling: 'touch', minWidth: 0 }}>
                      <pre className="stay-dark" style={{ margin: 0, color: '#f1f5f9', whiteSpace: 'pre-wrap', background: 'transparent' }}>
                        {p.code}
                      </pre>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={280}>
          <div style={{ marginTop: 40, textAlign: 'center' }}>
            <a
              href="technology.php"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                background: 'rgba(59,130,246,0.1)',
                border: '1px solid rgba(59,130,246,0.35)',
                color: 'var(--brand-soft)',
                padding: '12px 24px',
                borderRadius: 999,
                fontWeight: 600,
                fontSize: 14,
                textDecoration: 'none',
                transition: 'all .25s ease',
                boxShadow: '0 4px 20px rgba(59,130,246,0.15)',
                minHeight: 44,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(59,130,246,0.2)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(59,130,246,0.1)'; e.currentTarget.style.transform = 'none'; }}
            >
              <span>Explore Full Architecture</span>
              <SvgArrowRight />
            </a>
          </div>
        </Reveal>
      </div>
      <style>{`
        @media (max-width: 860px) {
          .timeline-card-split { grid-template-columns: 1fr !important; gap: 16px !important; }
        }
        @media (min-width: 861px) {
          .timeline-card-split { grid-template-columns: 1.2fr 1.8fr !important; gap: clamp(16px, 3vw, 28px) !important; }
        }
        @media (max-width: 520px) {
          .timeline-grid-row { grid-template-columns: 34px 1fr !important; gap: 12px !important; }
        }
        @media (min-width: 521px) {
          .timeline-grid-row { grid-template-columns: 44px 1fr !important; gap: clamp(14px, 3vw, 28px) !important; }
        }
      `}</style>
    </section>
  );
}

// ─── SECTION 3: SECURITY BENTO GRID ─────────────────────────────────────────
function SecurityBentoGrid() {
  return (
    <section id="features" style={{ padding: 'clamp(60px, 8vw, 100px) clamp(16px, 4vw, 32px)', position: 'relative' }}>
      <div style={{ maxWidth: 1240, margin: '0 auto' }}>
        <Reveal>
          <div style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto clamp(36px, 6vw, 60px)' }}>
            <h2 style={{ fontSize: 'clamp(28px, 4.5vw, 48px)', fontWeight: 500, letterSpacing: -1.5, color: 'var(--text)', marginBottom: 14, fontFamily: 'var(--font-ui)' }}>
              Multi-layered binary virtualization.
            </h2>
            <p style={{ color: 'var(--text-dim)', fontSize: 'clamp(14.5px, 2vw, 16.5px)', lineHeight: 1.6, fontFamily: 'var(--font-ui)' }}>
              Every layer removes a traditional attack vector, forcing reverse engineers below the Python tooling layer.
            </p>
          </div>
        </Reveal>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }} className="bento-grid">
          {BENTO_FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={i * 50}>
              <div
                style={{
                  background: 'var(--surface)', border: '1px solid var(--border-strong)',
                  borderRadius: 18, padding: 'clamp(20px, 3.5vw, 32px)', height: '100%', display: 'flex', flexDirection: 'column',
                  justifyContent: 'space-between', backdropFilter: 'blur(10px)', transition: 'all .3s ease',
                  gridColumn: f.large ? 'span 2' : 'span 1', minWidth: 0,
                }}
                className={`card bento-card ${f.large ? 'bento-large-card' : ''}`}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(59,130,246,0.45)'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.transform = 'none'; }}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ padding: 8, borderRadius: 10, background: 'var(--bg)', border: '1px solid var(--border)' }}>
                      {f.icon}
                    </div>
                    <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: 'var(--brand-soft)', fontFamily: 'var(--font-code)' }}>
                      {f.tag}
                    </span>
                  </div>
                  <h3 style={{ fontSize: 'clamp(18px, 2.5vw, 20px)', fontWeight: 600, color: 'var(--text)', letterSpacing: -0.4, marginBottom: 8, fontFamily: 'var(--font-ui)' }}>
                    {f.title}
                  </h3>
                  <p style={{ color: 'var(--text-dim)', fontSize: 14, lineHeight: 1.6, fontFamily: 'var(--font-ui)' }}>
                    {f.desc}
                  </p>
                  {f.large && (
                    <div style={{ marginTop: 16 }}>
                      <a
                        href="technology.php#vm-debugger"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          color: 'var(--brand-soft)',
                          fontSize: 13,
                          fontWeight: 600,
                          textDecoration: 'none',
                          transition: 'color .2s',
                          minHeight: 36,
                        }}
                      >
                        <span>Interactive VM Stepper Demo</span>
                        <SvgArrowRight size={13} />
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
      <style>{`
        @media (min-width: 1025px) {
          .bento-grid { grid-template-columns: repeat(3, 1fr) !important; }
          .bento-large-card { grid-column: span 2 !important; }
        }
        @media (max-width: 1024px) and (min-width: 721px) {
          .bento-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .bento-large-card { grid-column: span 2 !important; }
        }
        @media (max-width: 720px) {
          .bento-grid { grid-template-columns: 1fr !important; }
          .bento-grid > div, .bento-large-card { grid-column: span 1 !important; }
        }
      `}</style>
    </section>
  );
}

// ─── SECTION 4: COMPATIBILITY SECTION ───────────────────────────────────────
const COMPAT_ITEMS = [
  'async / await coroutines',
  'Generators & yield statements',
  'Closures & complex decorators',
  'Context managers (with blocks)',
  'f-strings & comprehensions',
  'Exception handling (try / except / finally)',
  'Third-party wheels (NumPy, PyTorch, Requests)',
  'Python 3.11 - 3.13 and 3.13t (no-GIL) support',
];

function CompatibilitySection() {
  return (
    <section id="compat" style={{ padding: 'clamp(50px, 7vw, 90px) clamp(16px, 4vw, 32px)', position: 'relative' }}>
      <div style={{ maxWidth: 1240, margin: '0 auto' }}>
        <Reveal>
          <div style={{ textAlign: 'center', maxWidth: 680, margin: '0 auto clamp(32px, 5vw, 50px)' }}>
            <h2 style={{ fontSize: 'clamp(28px, 4.5vw, 48px)', fontWeight: 500, letterSpacing: -1.5, color: 'var(--text)', marginBottom: 12, fontFamily: 'var(--font-ui)' }}>
              Works with your Python stack.
            </h2>
            <p style={{ color: 'var(--text-dim)', fontSize: 'clamp(14.5px, 2vw, 16.5px)', lineHeight: 1.6, fontFamily: 'var(--font-ui)' }}>
              Protected functions keep their signatures and call semantics — drop-in for callers, no rewrites required.
            </p>
          </div>
        </Reveal>

        <Reveal delay={80}>
          <div
            style={{
              maxWidth: 900, margin: '0 auto', background: 'var(--surface)', border: '1px solid var(--border-strong)',
              borderRadius: 18, padding: 'clamp(20px, 4vw, 36px)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 28px',
            }}
            className="card compat-two-col"
          >
            {COMPAT_ITEMS.map((item, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ color: '#10b981', flexShrink: 0 }}><SvgCheck /></span>
                <span style={{ fontSize: 14, color: 'var(--text-dim)', fontWeight: 500, fontFamily: 'var(--font-ui)' }}>{item}</span>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
      <style>{`
        @media (max-width: 680px) { .compat-two-col { grid-template-columns: 1fr !important; gap: 12px !important; padding: 20px !important; } }
        @media (min-width: 681px) { .compat-two-col { grid-template-columns: 1fr 1fr !important; gap: 14px 28px !important; padding: clamp(20px, 4vw, 36px) !important; } }
      `}</style>
    </section>
  );
}

// ─── SECTION 5: COMPETITOR COMPARISON GATEWAY ───────────────────────────────
function ComparisonGateway() {
  return (
    <section id="compare" style={{ padding: 'clamp(60px, 8vw, 100px) clamp(16px, 4vw, 32px)', position: 'relative' }}>
      <div style={{ maxWidth: 1240, margin: '0 auto' }}>
        <Reveal>
          <div style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto clamp(36px, 6vw, 60px)' }}>
            <h2 style={{ fontSize: 'clamp(28px, 4.5vw, 48px)', fontWeight: 500, letterSpacing: -1.5, color: 'var(--text)', marginBottom: 14, fontFamily: 'var(--font-ui)' }}>
              How PyK7VM compares.
            </h2>
            <p style={{ color: 'var(--text-dim)', fontSize: 'clamp(14.5px, 2vw, 16.5px)', lineHeight: 1.6, fontFamily: 'var(--font-ui)' }}>
              Factual technical evaluations against leading Python obfuscation and transpilation tools.
            </p>
          </div>
        </Reveal>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'clamp(18px, 3vw, 28px)' }} className="compare-cards-grid">
          {/* Card 1: PyArmor */}
          <Reveal delay={80}>
            <div
              style={{
                background: 'var(--surface)', border: '1px solid var(--border-strong)',
                borderRadius: 20, padding: 'clamp(20px, 4vw, 36px)', display: 'flex', flexDirection: 'column',
                justifyContent: 'space-between', height: '100%', backdropFilter: 'blur(10px)',
                transition: 'all .3s ease', minWidth: 0,
              }}
              className="card compare-card"
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(59,130,246,0.45)'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.transform = 'none'; }}
            >
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 6 }}>
                  <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--brand-soft)', fontFamily: 'var(--font-code)' }}>
                    Bytecode Obfuscation vs. VM
                  </span>
                  <span style={{ fontSize: 10.5, fontWeight: 700, color: '#10b981', background: 'rgba(16,185,129,0.1)', padding: '2px 8px', borderRadius: 999 }}>
                    Zero Bytecode
                  </span>
                </div>

                <h3 style={{ fontSize: 'clamp(20px, 3vw, 26px)', fontWeight: 600, color: 'var(--text)', letterSpacing: -0.6, marginBottom: 10, fontFamily: 'var(--font-ui)' }}>
                  PyK7VM <span style={{ color: 'var(--text-muted)', fontWeight: 300 }}>vs</span> PyArmor
                </h3>

                <p style={{ color: 'var(--text-dim)', fontSize: 14, lineHeight: 1.6, marginBottom: 20, fontFamily: 'var(--font-ui)' }}>
                  PyArmor distributes obfuscated .py files that decrypt bytecode payloads into CPython's eval frame. PyK7VM eliminates Python bytecode entirely, executing logic in an opaque native virtual machine.
                </p>

                <div style={{ display: 'grid', gap: 10, marginBottom: 24 }}>
                  <div className="compare-metric-row" style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 14px', fontSize: 12.5, fontFamily: 'var(--font-ui)' }}>
                    <div style={{ color: 'var(--text-muted)', fontSize: 10.5, textTransform: 'uppercase', marginBottom: 4, fontFamily: 'var(--font-code)' }}>Runtime Bytecode</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 4 }}>
                      <span style={{ color: '#f87171' }}>PyArmor: In-Memory</span>
                      <span style={{ color: '#10b981' }}>PyK7VM: 0 Bytecode</span>
                    </div>
                  </div>
                  <div className="compare-metric-row" style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 14px', fontSize: 12.5, fontFamily: 'var(--font-ui)' }}>
                    <div style={{ color: 'var(--text-muted)', fontSize: 10.5, textTransform: 'uppercase', marginBottom: 4, fontFamily: 'var(--font-code)' }}>CPython Frame Hooks</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 4 }}>
                      <span style={{ color: '#f87171' }}>PyArmor: Hookable</span>
                      <span style={{ color: '#10b981' }}>PyK7VM: No eval frame to hook</span>
                    </div>
                  </div>
                </div>
              </div>

              <a
                href="compare/pyarmor.php"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  background: 'rgba(59,130,246,0.1)', color: 'var(--brand-soft)', border: '1px solid rgba(59,130,246,0.25)',
                  padding: '12px 20px', borderRadius: 10, fontWeight: 600, fontSize: 13.5, textDecoration: 'none',
                  transition: 'background .2s', fontFamily: 'var(--font-ui)', minHeight: 44,
                }}
              >
                <span>View PyArmor Technical Matrix</span>
                <SvgArrowRight />
              </a>
            </div>
          </Reveal>

          {/* Card 2: Nuitka */}
          <Reveal delay={140}>
            <div
              style={{
                background: 'var(--surface)', border: '1px solid var(--border-strong)',
                borderRadius: 20, padding: 'clamp(20px, 4vw, 36px)', display: 'flex', flexDirection: 'column',
                justifyContent: 'space-between', height: '100%', backdropFilter: 'blur(10px)',
                transition: 'all .3s ease', minWidth: 0,
              }}
              className="card compare-card"
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(59,130,246,0.45)'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.transform = 'none'; }}
            >
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 6 }}>
                  <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--brand-soft)', fontFamily: 'var(--font-code)' }}>
                    C Transpilation vs. VM
                  </span>
                  <span style={{ fontSize: 10.5, fontWeight: 700, color: '#10b981', background: 'rgba(16,185,129,0.1)', padding: '2px 8px', borderRadius: 999 }}>
                    Anti-Reversing
                  </span>
                </div>

                <h3 style={{ fontSize: 'clamp(20px, 3vw, 26px)', fontWeight: 600, color: 'var(--text)', letterSpacing: -0.6, marginBottom: 10, fontFamily: 'var(--font-ui)' }}>
                  PyK7VM <span style={{ color: 'var(--text-muted)', fontWeight: 300 }}>vs</span> Nuitka
                </h3>

                <p style={{ color: 'var(--text-dim)', fontSize: 14, lineHeight: 1.6, marginBottom: 20, fontFamily: 'var(--font-ui)' }}>
                  Nuitka translates Python to C, preserving object semantics and leaving strings in plaintext. PyK7VM virtualizes logic inside an opaque VM while delivering native C speed on compute hot-paths.
                </p>

                <div style={{ display: 'grid', gap: 10, marginBottom: 24 }}>
                  <div className="compare-metric-row" style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 14px', fontSize: 12.5, fontFamily: 'var(--font-ui)' }}>
                    <div style={{ color: 'var(--text-muted)', fontSize: 10.5, textTransform: 'uppercase', marginBottom: 4, fontFamily: 'var(--font-code)' }}>Binary Control Flow</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 4 }}>
                      <span style={{ color: '#f87171' }}>Nuitka: Reconstructible C</span>
                      <span style={{ color: '#10b981' }}>PyK7VM: Opaque VM</span>
                    </div>
                  </div>
                  <div className="compare-metric-row" style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 14px', fontSize: 12.5, fontFamily: 'var(--font-ui)' }}>
                    <div style={{ color: 'var(--text-muted)', fontSize: 10.5, textTransform: 'uppercase', marginBottom: 4, fontFamily: 'var(--font-code)' }}>String Literals &amp; Keys</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 4 }}>
                      <span style={{ color: '#f87171' }}>Nuitka: Plaintext</span>
                      <span style={{ color: '#10b981' }}>PyK7VM: Encrypted</span>
                    </div>
                  </div>
                </div>
              </div>

              <a
                href="compare/nuitka.php"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  background: 'rgba(59,130,246,0.1)', color: 'var(--brand-soft)', border: '1px solid rgba(59,130,246,0.25)',
                  padding: '12px 20px', borderRadius: 10, fontWeight: 600, fontSize: 13.5, textDecoration: 'none',
                  transition: 'background .2s', fontFamily: 'var(--font-ui)', minHeight: 44,
                }}
              >
                <span>View Nuitka Technical Matrix</span>
                <SvgArrowRight />
              </a>
            </div>
          </Reveal>
        </div>
      </div>
      <style>{`
        @media (max-width: 860px) { .compare-cards-grid { grid-template-columns: 1fr !important; gap: 20px !important; } }
        @media (min-width: 861px) { .compare-cards-grid { grid-template-columns: 1fr 1fr !important; gap: clamp(18px, 3vw, 28px) !important; } }
      `}</style>
    </section>
  );
}

// ─── SECTION 6: CI/CD & DEVELOPER GATEWAY ───────────────────────────────────
function DeveloperPlayground() {
  return (
    <section id="api" style={{ padding: 'clamp(60px, 8vw, 100px) clamp(16px, 4vw, 32px)', position: 'relative' }}>
      <div style={{ maxWidth: 1040, margin: '0 auto' }}>
        <Reveal>
          <div
            style={{
              background: 'var(--surface)', border: '1px solid var(--border-strong)',
              borderRadius: 22, padding: 'clamp(24px, 5vw, 48px)', display: 'grid', gridTemplateColumns: '1.2fr 1fr',
              gap: 'clamp(24px, 4vw, 48px)', alignItems: 'center', boxShadow: '0 20px 50px rgba(0,0,0,0.5)', minWidth: 0,
            }}
            className="card api-split-box"
          >
            <div>
              <h2 style={{ fontSize: 'clamp(24px, 3.5vw, 40px)', fontWeight: 500, letterSpacing: -1.2, color: 'var(--text)', marginBottom: 12, fontFamily: 'var(--font-ui)' }}>
                Automate protection in CI/CD.
              </h2>
              <p style={{ color: 'var(--text-dim)', fontSize: 'clamp(14px, 2vw, 15.5px)', lineHeight: 1.6, marginBottom: 24, fontFamily: 'var(--font-ui)' }}>
                One API key. Integrate native binary compilation directly into GitHub Actions, GitLab CI, or custom deployment pipelines using our REST endpoints.
              </p>
              <a
                href="docs.php"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  background: 'var(--text)', color: 'var(--bg)', padding: '12px 24px',
                  borderRadius: 999, fontWeight: 600, fontSize: 13.5, textDecoration: 'none',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.18)', fontFamily: 'var(--font-ui)', minHeight: 44,
                }}
              >
                <span>View Full CI/CD Docs</span>
                <SvgArrowRight color="var(--bg)" />
              </a>
            </div>

            <div className="terminal-box stay-dark" style={{ background: '#090912', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14, padding: 'clamp(16px, 3vw, 22px)', fontFamily: 'var(--font-code)', fontSize: 11.5, lineHeight: 1.7, overflowX: 'auto', WebkitOverflowScrolling: 'touch', minWidth: 0, color: '#f1f5f9' }}>
              <div style={{ color: '#a1a1aa', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: 8, marginBottom: 10, fontSize: 10.5 }}>
                CI/CD PIPELINE EXAMPLE
              </div>
              <div style={{ color: '#93c5fd' }}># Submit to PyK7VM Cloud Engine</div>
              <div style={{ color: '#f1f5f9' }}>curl -X POST https://pyk7vm.com/api/v1/job_submit.php \</div>
              <div style={{ paddingLeft: 12, color: '#d4d4d8' }}>-H "Authorization: Bearer pk7vm_live_..." \</div>
              <div style={{ paddingLeft: 12, color: '#d4d4d8' }}>-F "source=@dist/my_module.py" \</div>
              <div style={{ paddingLeft: 12, color: '#d4d4d8' }}>-F "target=windows-x64"</div>
              <div style={{ color: '#10b981', marginTop: 6 }}># Returns job_id · Download sealed .pyd</div>
            </div>
          </div>
        </Reveal>
      </div>
      <style>{`
        @media (max-width: 860px) { .api-split-box { grid-template-columns: 1fr !important; gap: 24px !important; padding: 24px !important; } }
        @media (min-width: 861px) { .api-split-box { grid-template-columns: 1.2fr 1fr !important; gap: clamp(24px, 4vw, 48px) !important; padding: clamp(24px, 5vw, 48px) !important; } }
      `}</style>
    </section>
  );
}

// ─── SECTION 7: BOTTOM CTA ──────────────────────────────────────────────────
function BottomCTA() {
  return (
    <section style={{ padding: 'clamp(40px, 6vw, 80px) clamp(16px, 4vw, 24px) clamp(80px, 10vw, 120px)', position: 'relative' }}>
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <Reveal>
          <div
            className="bottom-cta-banner"
            style={{
              borderRadius: 24,
              padding: 'clamp(36px, 6vw, 64px) clamp(20px, 5vw, 48px)',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all .3s ease',
            }}
          >
            <h2 style={{ fontSize: 'clamp(26px, 4vw, 44px)', fontWeight: 500, letterSpacing: -1.2, color: 'var(--text)', marginBottom: 12, fontFamily: 'var(--font-ui)' }}>
              Seal your Python intellectual property today.
            </h2>
            <p style={{ color: 'var(--text-dim)', fontSize: 'clamp(14.5px, 2vw, 16.5px)', maxWidth: 560, margin: '0 auto 32px', lineHeight: 1.6, fontFamily: 'var(--font-ui)' }}>
              Join forward-thinking software vendors securing their proprietary logic with native binary virtualization.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <a
                href="register.php"
                className="cta-primary-btn"
                style={{
                  background: 'var(--text)', color: 'var(--bg)', padding: '13px 30px',
                  borderRadius: 999, fontWeight: 600, fontSize: 14.5, textDecoration: 'none',
                  letterSpacing: -0.2, boxShadow: '0 4px 20px rgba(0,0,0,0.2)', fontFamily: 'var(--font-ui)', minHeight: 46,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'transform .2s ease, box-shadow .2s ease, opacity .2s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.opacity = '0.92'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.opacity = '1'; }}
              >
                Create Free Account
              </a>
              <a
                href="docs.php"
                className="cta-secondary-btn"
                style={{
                  background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border-strong)',
                  padding: '13px 26px', borderRadius: 999, fontWeight: 500, fontSize: 14.5, textDecoration: 'none', fontFamily: 'var(--font-ui)', minHeight: 46,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all .2s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(59,130,246,0.5)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.transform = 'none'; }}
              >
                Read Documentation
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── MAIN APP ROOT ──────────────────────────────────────────────────────────
function MainApp() {
  // Initialize Lenis smooth scroll
  useEffect(() => {
    if (typeof window.Lenis !== 'undefined') {
      const lenis = new window.Lenis({
        duration: 0.8,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: true,
        touchMultiplier: 1.5,
      });
      window.__lenis = lenis;

      let rafId;
      function raf(time) {
        lenis.raf(time);
        rafId = requestAnimationFrame(raf);
      }
      rafId = requestAnimationFrame(raf);

      return () => {
        cancelAnimationFrame(rafId);
        lenis.destroy();
      };
    }
  }, []);

  return (
    <div style={{ position: 'relative', overflowX: 'hidden', width: '100%', maxWidth: '100vw' }}>
      <style>{`
        .bottom-cta-banner {
          background: radial-gradient(ellipse at center top, rgba(59,130,246,0.15) 0%, rgba(10,10,15,0.85) 70%);
          border: 1px solid rgba(59,130,246,0.3);
          box-shadow: 0 24px 80px rgba(0,0,0,0.6), 0 0 40px rgba(59,130,246,0.1);
        }
        html.light .timeline-card-split,
        html.light section[id="features"] > div > div:nth-child(2) > div,
        html.light .security-bento-card,
        html.light .bento-grid > div,
        html.light .compat-two-col,
        html.light .compare-cards-grid > div,
        html.light .api-split-box {
          background: #ffffff !important;
          border-color: rgba(15, 23, 42, 0.08) !important;
          box-shadow: 0 4px 20px -2px rgba(15, 23, 42, 0.05) !important;
          color: #0f172a !important;
        }
        html.light .timeline-grid-row > div:first-child > div:first-child {
          background: #ffffff !important;
        }
        html.light .hero-terminal-window,
        html.light .terminal-window {
          background: #09090f !important;
          border-color: rgba(255, 255, 255, 0.12) !important;
        }
        html.light .bottom-cta-banner {
          background: radial-gradient(ellipse at center top, rgba(239,246,255,0.95) 0%, #ffffff 75%) !important;
          border: 1px solid rgba(37,99,235,0.25) !important;
          box-shadow: 0 20px 60px rgba(15,23,42,0.08), 0 0 30px rgba(37,99,235,0.06) !important;
          color: #0f172a !important;
        }
        html.light .bottom-cta-banner h2 {
          color: #0f172a !important;
        }
        html.light .bottom-cta-banner p {
          color: #475569 !important;
        }
        html.light .cta-primary-btn {
          background: #0f172a !important;
          color: #ffffff !important;
          box-shadow: 0 4px 20px rgba(15,23,42,0.15) !important;
        }
        html.light .cta-secondary-btn {
          background: #ffffff !important;
          color: #0f172a !important;
          border: 1px solid rgba(15,23,42,0.16) !important;
          box-shadow: 0 2px 8px rgba(15,23,42,0.04) !important;
        }
        html.light .cta-secondary-btn:hover {
          border-color: rgba(37,99,235,0.4) !important;
          background: #f8fafc !important;
          color: #2563eb !important;
        }
      `}</style>
      <Nav />
      <Hero />
      <ArchitecturePipeline />
      <SecurityBentoGrid />
      <CompatibilitySection />
      <ComparisonGateway />
      <DeveloperPlayground />
      <BottomCTA />
      <Footer />
      <DiscordCTA />
    </div>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(React.createElement(MainApp));
